import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { get } from "common/helpers/HTTP";
import type { EmailOutreachType } from "common/types";

import { useCampaignContext } from "../context/CampaignContext";

import type { CsvState } from "../types";

type LeadsState = {
  state: "failed" | "in_progress" | "success";
  leads: Omit<CsvState, "fileName" | "mainIdentifier">;
};

async function getCrmLeadsStateRequest(
  workspaceId: string,
  type: EmailOutreachType,
  sourceId: string,
): Promise<LeadsState> {
  const { state, leads } = await get(
    `workspaces/${workspaceId}/integrations/${type}/${type === "instantly" ? "lists" : "campaigns"}/${sourceId}/leads`,
  );

  return { state, leads };
}

export default function useCrmLeads(sourceId: string) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { id: campaignId, crm_type: type } = useCampaignContext();
  const { campaignsKeys } = getQueryKeys(workspaceId);

  const { data: crmLeadsState, isLoading: isLoadingCrmLeadsState } = useQuery({
    queryKey: campaignsKeys.crmLeads(campaignId, sourceId, type),
    queryFn: () => getCrmLeadsStateRequest(workspaceId, type, sourceId),
    refetchInterval: ({ state: { data } }) =>
      data?.state === "in_progress" ? 3000 : false, // Poll every 3s if still processing
    refetchIntervalInBackground: true,
  });

  return {
    crmLeadsState,
    isLoadingCrmLeadsState,
  };
}
